import { RelayEnvironment } from '@/graphql/relay-env';
import { graphql } from 'react-relay';
import { type PayloadError, commitMutation } from 'relay-runtime';
import { postStatusUpdater } from '../updaters.utils';
import { genericMutation } from '../utils';
import type { Content_Create_Mutation } from './__generated__/Content_Create_Mutation.graphql';
import type { Content_Duplicate_Mutation } from './__generated__/Content_Duplicate_Mutation.graphql';
import type {
  Content_UpdateStatus_Mutation,
  Content_UpdateStatus_Mutation$variables,
} from './__generated__/Content_UpdateStatus_Mutation.graphql';
import type {
  Content_Update_Mutation,
  Content_Update_Mutation$data,
  Content_Update_Mutation$variables,
} from './__generated__/Content_Update_Mutation.graphql';

// https://relay.dev/docs/guided-tour/list-data/updating-connections/#using-declarative-directives
const CreateContentMutation = graphql`
  mutation Content_Create_Mutation($input: CreateZnglyVideoInput!, $connections: [ID!]!) {
    createZnglyVideo(input: $input) {
      znglyVideo @prependNode(connections: $connections, edgeTypeName: "ZnglyVideo") {
        ...ZnglyVideo_Pagination_Node_Fragment @relay(mask: false)
      }
    }
  }
`;

export const createContentMutation = genericMutation<Content_Create_Mutation>({
  mutation: CreateContentMutation,
});

const UpdateContentMutation = graphql`
  mutation Content_Update_Mutation($input: UpdateZnglyVideoInput!) {
    updateZnglyVideo(input: $input) {
      znglyVideo {
        ...Content_Single_Fragment @relay(mask: false)
      }
    }
  }
`;

export const updateContentMutation = (
  input: Content_Update_Mutation$variables['input'],
  onCompleted?: (
    response: Content_Update_Mutation$data | null,
    errors: readonly PayloadError[] | null | undefined
  ) => void
) => {
  commitMutation<Content_Update_Mutation>(RelayEnvironment, {
    mutation: UpdateContentMutation,
    variables: {
      input: {
        ...input,
        slug: input?.title || null,
      },
    },
    onCompleted,
    onError: (error) => {
      onCompleted?.(null, [error]);
    },
    updater: (store, data) => {
      if (!data?.updateZnglyVideo?.znglyVideo?.id || !data?.updateZnglyVideo?.znglyVideo?.status) return;
      postStatusUpdater(
        store,
        [data?.updateZnglyVideo?.znglyVideo?.id],
        'Catalog',
        data?.updateZnglyVideo?.znglyVideo?.status
      );
    },
  });
};

const UpdateContentStatusMutation = graphql`
  mutation Content_UpdateStatus_Mutation($input: UpdateZnglyVideoInput!) @raw_response_type {
    updateZnglyVideo(input: $input) {
      znglyVideo {
        id
        status
      }
    }
  }
`;

export const updateContentStatusMutation = (input: Content_UpdateStatus_Mutation$variables['input']) => {
  commitMutation<Content_UpdateStatus_Mutation>(RelayEnvironment, {
    mutation: UpdateContentStatusMutation,
    variables: {
      input,
    },
    optimisticResponse: {
      updateZnglyVideo: {
        znglyVideo: {
          ...input,
          status: input.status?.toLowerCase(),
        },
      },
    },
  });
};

export * from './__generated__/Content_Duplicate_Mutation.graphql';
const DuplicateContentMutation = graphql`
  mutation Content_Duplicate_Mutation($input: DuplicateZnglyVideoInput!, $connections: [ID!]!) {
    duplicateZnglyVideo(input: $input) {
      znglyVideo @prependNode(connections: $connections, edgeTypeName: "ZnglyVideo") {
        ...Content_Single_Fragment @relay(mask: false)
      }
    }
  }
`;

export const duplicateContentMutation = genericMutation<Content_Duplicate_Mutation>({
  mutation: DuplicateContentMutation,
});
